// Dashboard — KPI grid, status funnel, recent-activity feed, and the subscription
// return table. Ported from the prototype (crm/dashboard.jsx); all derived numbers
// come straight from the server's DashboardSummary instead of local recomputation.

import { useQuery } from '@tanstack/react-query';
import { BILLING_OPTIONS } from '@delvero/contracts';
import type { Activity } from '@delvero/contracts';
import { orpc } from '../../lib/orpc';
import { fmtZl, fmtDur, fmtMin, fmtPayback, relDate } from '../../lib/format';
import { useShell } from '../../shell';
import { Icon } from '../../components/Icon';
import type { IconName } from '../../components/Icon';
import { Kpi } from '../../components/Kpi';

function feedIcon(type: Activity['type']): IconName {
  if (type === 'call') return 'phone';
  if (type === 'email') return 'mailIc';
  if (type === 'status') return 'flag';
  return 'noteIc';
}

export function DashboardView() {
  const { activeAppId, openLead } = useShell();
  const summaryQuery = useQuery({
    ...orpc.dashboard.summary.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });

  if (!activeAppId || summaryQuery.isLoading || !summaryQuery.data) {
    return (
      <div className="view">
        <div className="card center-pad">
          <span className="spinner" /> Wczytywanie…
        </div>
      </div>
    );
  }

  const { kpis, funnel, recent, subs } = summaryQuery.data;
  const maxCount = Math.max(1, ...funnel.map((f) => f.count));

  return (
    <div className="view">
      <div className="kpi-grid">
        <Kpi label="MRR" value={fmtZl(kpis.mrr)} sub={`${kpis.subCount} aktywne suby`} accent />
        <Kpi label="Śr. CAC suba" value={kpis.subCount ? fmtZl(kpis.avgCac) : '—'} sub="czas + reklama" />
        <Kpi
          label="Zwrot łącznie"
          value={(kpis.totalRet >= 0 ? '+' : '') + fmtZl(kpis.totalRet)}
          sub="przychód − CAC"
          accent={kpis.totalRet >= 0}
        />
        <Kpi label="Calle" value={kpis.callCount} sub={`${fmtMin(kpis.callSec)} na słuchawce`} />
        <Kpi label="Konwersja" value={`${kpis.conversion}%`} sub={`${kpis.subCount} z ${kpis.closedCount} domkniętych`} />
        <Kpi label="Leady" value={kpis.leadCount} sub={`${kpis.newCount} nowych do ruszenia`} />
      </div>

      <div className="dash-cols">
        <div className="card pad">
          <div className="card-title">Lejek — statusy</div>
          {funnel.map((f) => (
            <div className="funnel-row" key={f.id}>
              <span className="funnel-label">{f.label}</span>
              <span className="funnel-track">
                <span
                  className="funnel-bar"
                  style={{ width: `${Math.max(2, (f.count / maxCount) * 100)}%`, background: f.color }}
                />
              </span>
              <span className="mono funnel-num">{f.count}</span>
            </div>
          ))}
        </div>

        <div className="card pad">
          <div className="card-title">Ostatnia aktywność</div>
          {recent.map(({ activity, leadId, company }) => (
            <button className="feed-row" key={activity.id} onClick={() => openLead(leadId)}>
              <span className="feed-type">
                <Icon name={feedIcon(activity.type)} size={12} />
              </span>
              <span className="feed-body">
                <span className="cell-main">
                  {company}
                  {activity.type === 'call' && activity.durationSec != null ? (
                    <span className="mono tl-dur"> {fmtDur(activity.durationSec)}</span>
                  ) : null}
                </span>
                <span className="cell-sub">{activity.note || activity.outcome || '—'}</span>
              </span>
              <span className="tl-date">{relDate(activity.at)}</span>
            </button>
          ))}
          {recent.length === 0 ? <div className="empty-cell">Jeszcze nic się nie wydarzyło.</div> : null}
        </div>
      </div>

      <div className="card pad">
        <div className="card-title">Klienci z subskrypcją — zwrot</div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Firma</th>
              <th>Plan</th>
              <th className="num">MRR</th>
              <th className="num">Mies.</th>
              <th className="num">Przychód</th>
              <th className="num">CAC</th>
              <th className="num">Zwrot</th>
              <th className="num">Payback</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((l) => {
              const billingLabel = BILLING_OPTIONS.find((b) => b.id === l.billing)?.label ?? '';
              const ret = l.econ.ret ?? 0;
              return (
                <tr key={l.id} onClick={() => openLead(l.id)}>
                  <td>
                    <div className="cell-main">{l.company}</div>
                  </td>
                  <td>
                    {l.planName ?? '—'} <span className="cell-sub">{billingLabel.toLowerCase()}</span>
                  </td>
                  <td className="num mono">{fmtZl(l.econ.mrr)}</td>
                  <td className="num mono">{l.econ.months}</td>
                  <td className="num mono">{fmtZl(l.econ.revenue)}</td>
                  <td className="num mono">{fmtZl(l.econ.cac)}</td>
                  <td className={`num mono ${ret >= 0 ? 'acc' : 'neg'}`}>{(ret >= 0 ? '+' : '') + fmtZl(ret)}</td>
                  <td className="num mono">{fmtPayback(l.econ.payback)}</td>
                </tr>
              );
            })}
            {subs.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-cell">
                  Brak aktywnych subskrypcji.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
